import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { showToast, toastApiError } from '@doccareplus/kernel/application';
import { Button, Card, DateField, EmptyState, Modal, SearchSelect, SkeletonCard, Textarea, TimeField } from '@doccareplus/ui';
import { statusKey, statusTone, isActionable, formatAppointmentDateTime, formatSentAgo } from '../domain/appointmentStatus';
import {
  todayDateInput,
  combineDateTimeToIso,
  isBookingInPast,
  maxBookableDateInput,
  minSelectableTimeInput,
} from '../domain/appointmentForm';
import type { PortalAppointmentDto } from '../infrastructure/appointments.api';
import { useMyAppointmentsQuery, usePortalBookingOptionsQuery } from '../application/appointments.query';
import {
  useCancelAppointmentMutation,
  useRequestAppointmentMutation,
  useRequestRescheduleMutation,
} from '../application/appointments.command';
import { AppointmentsPageSkeleton } from './AppointmentsSkeletons';

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('portal');
  return (
    <span
      data-testid="appointment-status"
      className={`inline-flex items-center rounded-full px-[9px] py-0.5 text-[11.5px] font-semibold whitespace-nowrap ${statusTone(status)}`}
    >
      {t(`appointments.status.${statusKey(status)}`, status)}
    </span>
  );
}

function RescheduleLifecycle({ appointment }: { appointment: PortalAppointmentDto }) {
  const { t } = useTranslation('portal');
  const request = appointment.rescheduleRequest;
  if (!request) return null;

  const label = request.status === 'Pending'
    ? t('appointments.rescheduleLifecycle.pending', 'Reschedule awaiting clinic review')
    : request.status === 'Approved'
      ? t('appointments.rescheduleLifecycle.approved', 'Reschedule approved')
      : t('appointments.rescheduleLifecycle.rejected', 'Reschedule declined');

  return (
    <div className="mt-2 rounded-[9px] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-[12px]">
      <div className="font-semibold text-[var(--text)]">{label}</div>
      <div className="mt-0.5 text-[var(--text-2)]">
        {formatAppointmentDateTime(request.proposedStartTime)}
      </div>
      {request.reason && <div className="mt-0.5 text-[var(--text-3)]">{request.reason}</div>}
    </div>
  );
}

function PendingCard({
  appointment,
  onReschedule,
  onCancel,
}: {
  appointment: PortalAppointmentDto;
  onReschedule?: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('portal');
  return (
    <Card className="border-[var(--warning)] bg-[var(--warning-bg)] p-3.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[14.5px] font-bold">{formatAppointmentDateTime(appointment.startTime)}</span>
        <StatusBadge status={appointment.status} />
      </div>
      {(appointment.reason ?? appointment.patientNote) && (
        <div className="text-[13px] text-[var(--text-2)]">{appointment.reason ?? appointment.patientNote}</div>
      )}
      <div className="mt-1 text-[12px] text-[var(--text-3)]">
        {t('appointments.sentAgo', { time: formatSentAgo(appointment.createdAt) })}
      </div>
      <div className="mt-3 flex gap-2">
        {onReschedule && (
          <Button variant="outline" size="sm" fullWidth onClick={onReschedule}>
            {t('appointments.reschedule', 'Reschedule')}
          </Button>
        )}
        <Button variant="danger" size="sm" fullWidth onClick={onCancel}>
          {t('appointments.cancel', 'Cancel')}
        </Button>
      </div>
    </Card>
  );
}

function UpcomingCard({
  appointment,
  onReschedule,
  onCancel,
}: {
  appointment: PortalAppointmentDto;
  onReschedule?: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('portal');
  return (
    <Card className="p-3.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[14.5px] font-bold">{formatAppointmentDateTime(appointment.startTime)}</span>
        <StatusBadge status={appointment.status} />
      </div>
      {(appointment.reason ?? appointment.patientNote) && (
        <div className="text-[13px] text-[var(--text-2)]">{appointment.reason ?? appointment.patientNote}</div>
      )}
      <RescheduleLifecycle appointment={appointment} />
      {isActionable(appointment.status) && (
        <div className="mt-3 flex gap-2">
          {onReschedule && appointment.rescheduleRequest?.status !== 'Pending' && (
            <Button variant="outline" size="sm" fullWidth onClick={onReschedule}>
              {t('appointments.reschedule', 'Reschedule')}
            </Button>
          )}
          <Button variant="danger" size="sm" fullWidth onClick={onCancel}>
            {t('appointments.cancel', 'Cancel')}
          </Button>
        </div>
      )}
    </Card>
  );
}

function PastCard({ appointment }: { appointment: PortalAppointmentDto }) {
  return (
    <Card className="p-3 opacity-85">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13.5px] font-semibold">{formatAppointmentDateTime(appointment.startTime)}</span>
        <StatusBadge status={appointment.status} />
      </div>
      {(appointment.reason ?? appointment.patientNote) && (
        <div className="mt-0.5 text-[12.5px] text-[var(--text-3)]">{appointment.reason ?? appointment.patientNote}</div>
      )}
    </Card>
  );
}

function BookAppointmentModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('portal');
  const request = useRequestAppointmentMutation();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const options = usePortalBookingOptionsQuery(providerId, serviceId);
  const invalid = !providerId || !startTime || options.isFetching;

  const submit = () => {
    if (!providerId || !startTime) return;
    request.mutate(
      { startTime, providerId, serviceId, reason: reason.trim() || null },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: t('appointments.toasts.requested') });
          onClose();
        },
        onError: (error) => toastApiError(error, t('appointments.toasts.requestFailed')),
      }
    );
  };

  return (
    <Modal
      title={t('appointments.book.title', 'Book / request appointment')}
      description={t('appointments.book.subtitle', "We'll confirm your slot and notify you.")}
      onClose={onClose}
      footer={
        <Button
          fullWidth
          isLoading={request.isPending}
          disabled={invalid || request.isPending}
          onClick={submit}
        >
          {t('appointments.book.submit', 'Send request')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3.5">
        {options.isLoading ? (
          <SkeletonCard />
        ) : options.isError ? (
          <div role="alert" className="rounded-[12px] border border-[var(--danger)] bg-[var(--danger-bg)] p-3 text-[13px]">
            <p>{t('appointments.book.loadFailed', 'Available appointments could not be loaded.')}</p>
            <Button className="mt-2" variant="outline" size="sm" onClick={() => void options.refetch()}>
              {t('actions.retry', 'Try again')}
            </Button>
          </div>
        ) : options.data ? (
          <>
            <fieldset>
              <legend className="mb-2 text-[13px] font-semibold">
                {t('appointments.book.provider', 'Choose a doctor')}
              </legend>
              {options.data.providers.length === 0 ? (
                <EmptyState
                  title={t('appointments.book.noProviders', 'No doctors are currently available for patient booking.')}
                />
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {options.data.providers.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      aria-pressed={providerId === provider.id}
                      onClick={() => {
                        setProviderId(provider.id);
                        setStartTime(null);
                      }}
                      className={`rounded-[12px] border p-3 text-left transition-colors ${
                        providerId === provider.id
                          ? 'border-[var(--primary)] bg-[var(--primary-soft)]'
                          : 'border-[var(--border)] bg-[var(--surface)] hover:border-[var(--border-strong)]'
                      }`}
                    >
                      <span className="block text-[13.5px] font-semibold">{provider.name}</span>
                      {provider.specialty && (
                        <span className="mt-0.5 block text-[12px] text-[var(--text-3)]">{provider.specialty}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </fieldset>

            <SearchSelect
              label={t('appointments.book.service', 'Service (optional)')}
              value={serviceId ?? ''}
              onChange={(value) => {
                setServiceId(value || null);
                setStartTime(null);
              }}
              options={[
                { value: '', label: t('appointments.book.anyService', 'General appointment') },
                ...options.data.services.map((service) => ({ value: service.id, label: service.name })),
              ]}
              searchable={false}
              appearance="input"
              triggerAriaLabel={t('appointments.book.service', 'Service (optional)')}
            />

            {providerId && (
              <fieldset>
                <legend className="mb-2 text-[13px] font-semibold">
                  {t('appointments.book.availableSlots', 'Choose an available appointment')}
                </legend>
                {options.isFetching ? (
                  <SkeletonCard />
                ) : options.data.days.length === 0 ? (
                  <EmptyState
                    title={t('appointments.book.noSlots', 'No available appointments in the next 14 days.')}
                    action={
                      <Button variant="outline" size="sm" onClick={() => void options.refetch()}>
                        {t('actions.retry', 'Refresh availability')}
                      </Button>
                    }
                  />
                ) : (
                  <div className="max-h-[280px] space-y-3 overflow-y-auto pr-1">
                    {options.data.days.map((day) => (
                      <div key={day.date}>
                        <div className="mb-1.5 text-[12px] font-semibold text-[var(--text-2)]">
                          {new Intl.DateTimeFormat(undefined, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            timeZone: 'UTC',
                          }).format(new Date(`${day.date}T12:00:00Z`))}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {day.slots.map((slot) => (
                            <button
                              key={slot.startTime}
                              type="button"
                              aria-pressed={startTime === slot.startTime}
                              onClick={() => setStartTime(slot.startTime)}
                              className={`rounded-[10px] border px-3 py-2 text-[12.5px] font-semibold ${
                                startTime === slot.startTime
                                  ? 'border-[var(--primary)] bg-[var(--primary)] text-white'
                                  : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:border-[var(--primary)]'
                              }`}
                            >
                              {new Intl.DateTimeFormat(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZone: options.data.timezone,
                              }).format(new Date(slot.startTime))}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </fieldset>
            )}
          </>
        ) : null}
        <Textarea
          label={t('appointments.book.reason', 'Reason (optional)')}
          placeholder={t('appointments.book.reasonPlaceholder', 'e.g. Follow-up, fever, general checkup')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}

function CancelAppointmentModal({ appointment, onClose }: { appointment: PortalAppointmentDto; onClose: () => void }) {
  const { t } = useTranslation('portal');
  const cancel = useCancelAppointmentMutation();
  const [reason, setReason] = useState('');

  return (
    <Modal
      title={t('appointments.cancelDialog.title', 'Cancel this appointment?')}
      description={t('appointments.cancelDialog.description', 'Let the clinic know why, so they can free up the slot.')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" fullWidth disabled={cancel.isPending} onClick={onClose}>
            {t('appointments.cancelDialog.keep', 'Keep appointment')}
          </Button>
          <Button
            variant="danger"
            fullWidth
            isLoading={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { appointmentId: appointment.id, body: { reason: reason.trim() || null } },
                {
                  onSuccess: () => {
                    showToast({ type: 'success', message: t('appointments.toasts.cancelled') });
                    onClose();
                  },
                  onError: (error) => toastApiError(error, t('appointments.toasts.cancelFailed')),
                }
              )
            }
          >
            {t('appointments.cancelDialog.confirm', 'Cancel appointment')}
          </Button>
        </>
      }
    >
      <Textarea
        placeholder={t('appointments.cancelDialog.reasonPlaceholder', 'Reason (optional)')}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={3}
      />
    </Modal>
  );
}

function RescheduleAppointmentModal({ appointment, onClose }: { appointment: PortalAppointmentDto; onClose: () => void }) {
  const { t } = useTranslation('portal');
  const reschedule = useRequestRescheduleMutation();
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const iso = date && time ? combineDateTimeToIso(date, time) : null;
  const invalid = !iso || isBookingInPast(date ?? '', time ?? '');

  return (
    <Modal
      title={t('appointments.rescheduleDialog.title', 'Request a new time')}
      description={t('appointments.rescheduleDialog.description', 'The clinic will review and confirm your new time.')}
      onClose={onClose}
      footer={
        <Button
          fullWidth
          isLoading={reschedule.isPending}
          disabled={invalid || reschedule.isPending}
          onClick={() => {
            if (!iso) return;
            reschedule.mutate(
              {
                appointmentId: appointment.id,
                status: appointment.status,
                body: { proposedStartTime: iso, reason: reason.trim() || null },
              },
              {
                onSuccess: () => {
                  showToast({ type: 'success', message: t('appointments.toasts.rescheduleRequested') });
                  onClose();
                },
                onError: (error) => toastApiError(error, t('appointments.toasts.rescheduleFailed')),
              }
            );
          }}
        >
          {t('appointments.rescheduleDialog.submit', 'Send request')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3.5">
        <DateField
          label={t('appointments.rescheduleDialog.date', 'New date')}
          value={date}
          onChange={setDate}
          min={todayDateInput()}
          max={maxBookableDateInput()}
          requiredMark
        />
        <TimeField
          label={t('appointments.rescheduleDialog.time', 'New time')}
          value={time}
          onChange={setTime}
          minTime={minSelectableTimeInput(date ?? '')}
          requiredMark
        />
        <Textarea
          label={t('appointments.rescheduleDialog.reason', 'Reason (optional)')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}

export function AppointmentsPage() {
  const { t } = useTranslation('portal');
  const appointments = useMyAppointmentsQuery();
  const activeProfile = useOutletContext<{ isArchived?: boolean } | null>();
  const isArchived = Boolean(activeProfile?.isArchived);
  const [booking, setBooking] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PortalAppointmentDto | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<PortalAppointmentDto | null>(null);

  if (appointments.isLoading) {
    return <AppointmentsPageSkeleton label={t('appointments.loading', 'Loading your appointments')} />;
  }

  const pending = appointments.data?.pending ?? [];
  const upcoming = appointments.data?.upcoming ?? [];
  const past = appointments.data?.past ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[23px] font-bold tracking-[-0.01em] md:hidden">{t('appointments.title', 'Appointments')}</h1>
        {!isArchived && <Button variant="primary" fullWidth className="sm:w-auto" onClick={() => setBooking(true)}>
          <span aria-hidden="true">＋</span>
          {t('appointments.bookNew', 'Request appointment')}
        </Button>}
      </div>

      {isArchived && (
        <div role="status" className="rounded-[12px] border border-[var(--warning)] bg-[var(--warning-bg)] px-4 py-3 text-[13px]">
          {t('appointments.archivedReadOnly', 'This archived clinic record is read-only. Historical appointments remain available.')}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          {pending.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)]">
                {t('appointments.pending', 'Awaiting confirmation')}
              </h2>
              {pending.map((appointment) => (
                <PendingCard
                  key={appointment.id}
                  appointment={appointment}
                  onReschedule={isArchived ? undefined : () => setRescheduleTarget(appointment)}
                  onCancel={() => setCancelTarget(appointment)}
                />
              ))}
            </section>
          )}

          <section className="flex flex-col gap-3">
            <h2 className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)]">
              {t('appointments.upcoming', 'Upcoming')}
            </h2>
            {upcoming.length > 0 ? (
              upcoming.map((appointment) => (
                <UpcomingCard
                  key={appointment.id}
                  appointment={appointment}
                  onReschedule={isArchived ? undefined : () => setRescheduleTarget(appointment)}
                  onCancel={() => setCancelTarget(appointment)}
                />
              ))
            ) : (
              <p className="rounded-[14px] border border-dashed border-[var(--border-strong)] p-4 text-center text-[13px] text-[var(--text-3)]">
                {t('appointments.noneUpcoming', 'No upcoming appointments yet.')}
              </p>
            )}
          </section>
        </div>

        <section className="flex flex-col gap-2.5">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)]">
            {t('appointments.past', 'Past')}
          </h2>
          {past.length > 0 ? (
            past.map((appointment) => <PastCard key={appointment.id} appointment={appointment} />)
          ) : (
            <p className="rounded-[14px] border border-dashed border-[var(--border-strong)] p-4 text-center text-[13px] text-[var(--text-3)]">
              {t('appointments.nonePast', 'No past appointments yet.')}
            </p>
          )}
        </section>
      </div>

      {booking && <BookAppointmentModal onClose={() => setBooking(false)} />}
      {cancelTarget && <CancelAppointmentModal appointment={cancelTarget} onClose={() => setCancelTarget(null)} />}
      {rescheduleTarget && (
        <RescheduleAppointmentModal appointment={rescheduleTarget} onClose={() => setRescheduleTarget(null)} />
      )}
    </div>
  );
}
